import React, {MouseEventHandler, ReactElement} from 'react';
import {concatenate} from "services/string";
import Spinner from "components/Spinner";

enum Size {TINY, X_SMALL, SMALL, MEDIUM, LARGE, X_LARGE}

export enum Color {PRIMARY, SECONDARY, WHITE, RED}

type Props = {
    type?: 'submit' | 'reset' | 'button' | undefined;
    color?: Color
    icon?: (props: React.SVGProps<SVGSVGElement>) => ReactElement
    showSpinner?: boolean
    size?: Size;
    onClick?: MouseEventHandler<HTMLButtonElement>,
    disabled?: boolean,
    title?: string,
    children?: React.ReactNode;
};

const sizeClasses: Record<Size, string> = {
    [Size.TINY]: 'px-3 py-1 text-xs shadow-sm',
    [Size.X_SMALL]: 'px-3.5 py-1.5 text-xs shadow-md',
    [Size.SMALL]: 'px-4 py-2 text-sm leading-4 shadow-md',
    [Size.MEDIUM]: 'px-5 py-2 text-sm shadow-md',
    [Size.LARGE]: 'px-6 py-2 text-base shadow-md',
    [Size.X_LARGE]: 'px-7 py-3 text-base shadow-lg',
}

const iconSizeClasses: Record<Size, string> = {
    [Size.TINY]: 'h-4 w-4',
    [Size.X_SMALL]: 'h-4 w-4',
    [Size.SMALL]: 'h-5 w-5',
    [Size.MEDIUM]: 'h-5 w-5',
    [Size.LARGE]: 'h-5 w-5',
    [Size.X_LARGE]: 'h-5 w-5',
}

const colorClasses: Record<Color, string> = {
    [Color.PRIMARY]: 'border border-transparent text-white bg-brand hover:bg-gradient-white-overlay focus-visible:ring-brand shadow-brand/40',
    [Color.SECONDARY]: 'border border-transparent text-brand bg-brand/20 hover:bg-gradient-white-overlay focus-visible:ring-brand shadow-brand/30',
    [Color.WHITE]: 'border border-gray-200 text-gray-900 bg-white hover:bg-gray-100 focus-visible:ring-brand shadow-none',
    [Color.RED]: 'border border-transparent text-white bg-red-600 hover:bg-gradient-white-overlay focus-visible:ring-red-600 shadow-red-900/30',
}

const iconColorClasses: Record<Color, string> = {
    [Color.PRIMARY]: 'text-white',
    [Color.SECONDARY]: 'text-brand',
    [Color.WHITE]: 'text-gray-700',
    [Color.RED]: 'text-white',
}

const spinnerColors = {
    [Color.PRIMARY]: Spinner.colors.LIGHT,
    [Color.SECONDARY]: Spinner.colors.BRAND,
    [Color.WHITE]: Spinner.colors.NEUTRAL,
    [Color.RED]: Spinner.colors.LIGHT,
}

const disabledClasses = "text-gray-500 bg-gray-300 border border-transparent cursor-not-allowed shadow-none"
const baseClasses = "inline-flex gap-2 items-center font-medium rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"

function Button(props: Props) {
    const {type = 'button', color = Color.PRIMARY, showSpinner = false, onClick, disabled = false, size = Size.MEDIUM, children, title} = props

    const icon = props.icon? <props.icon className={concatenate(disabled ? 'text-gray-500' : iconColorClasses[color], iconSizeClasses[size])} /> : null
    const spinner = <Spinner color={disabled ? Spinner.colors.NEUTRAL : spinnerColors[color]} size={Spinner.sizes.X_SMALL}/>

    return (
        <button
            type={type}
            className={concatenate(baseClasses, sizeClasses[size], disabled ? disabledClasses : colorClasses[color])}
            onClick={onClick}
            disabled={disabled}
        title={title}
        >

            {icon || showSpinner?
                <div>
                    {showSpinner? spinner : icon}
                </div>
            : null}

            {children}
        </button>
    );
}

Button.sizes = Size
Button.colors = Color

export default Button;